import React, { useContext, useEffect, useState } from 'react';

// import package for map
import { Map, FeatureGroup, LayersControl, Marker, Popup, TileLayer, Tooltip, ZoomControl } from 'react-leaflet';

import HeatmapLayer from 'react-leaflet-heatmap-layer';
import useSupercluster from 'use-supercluster';
import L from 'leaflet';

// config
import { mapDefault, heatmapDefault, pointMarkerPalette } from '../config/config';

// components
import SearchInput from './SearchInput';

// store
import { StoreContext } from '../../store/StoreProvider';
import Act from '../../store/actions';

// using for getting ref to map container
// eslint-disable-next-line no-underscore-dangle
let _OpenStreetMap;

// get the map center [lat, long]
const getCenter = (mapData) => {
	const center = { lat: 0.0, long: 0.0 };
	mapData.forEach((item) => {
		center.lat += item.position[0];
		center.long += item.position[1];
	});
	return [center.lat / mapData.length, center.long / mapData.length];
};

// marker icon background color generator
const markerIconColor = (count, paletteStyle) => {
	const pointPalette = pointMarkerPalette[paletteStyle];
	const colorFound = pointPalette.find((pp) => count >= pp.min && count <= pp.max);
	if (colorFound !== null) {
		return colorFound.color;
	}
	return pointPalette[0].color;
};

// generate cluster icon
const clusterIcons = {};
const fetchClusterIcon = ({ count, size, paletteStyle }) => {
	const clusterColor = markerIconColor(count, paletteStyle);
	const iconHtml = `<div class="cluster-marker-icon" style="width: ${size}px; height: ${size}px; background: ${clusterColor}">
        <span style="font-weight: bold">${count}</span>          
      </div>`;
	clusterIcons[count] = L.divIcon({
		html: iconHtml,
	});
	return clusterIcons[count];
};

// generate point icon
const pointIcons = {};
const fetchPointIcon = ({ count, size, locName, paletteStyle }) => {
	const color = markerIconColor(count, paletteStyle);

	const str = locName === '' ? `${count}` : `<br/>${count}<br/>${locName}`;
	const iconHtml = `<div class="point-marker-icon" style="width: ${size}px;height: ${size}px; text-align: center; background: ${color}">
        ${str}
      </div>`;
	pointIcons[count] = L.divIcon({
		html: iconHtml,
	});
	return pointIcons[count];
};

// generate cluster
const clusterGenerator = ({
	clusterIndex,
	cluster,
	latitude,
	longitude,
	pointCount,
	clusterPoints,
	supercluster,
	paletteStyle,
}) => (
	<Marker
		className="clusterMarker"
		key={`cluster-${clusterIndex}-pointCount-${pointCount}`}
		position={[latitude, longitude]}
		icon={fetchClusterIcon({
			count: cluster.properties.intensity,
			size: 20 + (cluster.properties.intensity / clusterPoints.length) * 100,
			paletteStyle,
		})}
		onClick={() => {
			// zoom level in when click
			const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(cluster.id), 15);
			const leaflet = _OpenStreetMap.leafletElement;
			leaflet.setView([latitude, longitude], expansionZoom, {
				animate: true,
			});
		}}
	>
		<Tooltip className="mraker-tooltip">
			<span style={{ fontWeight: 'bold' }}>{cluster.properties.location}</span>
		</Tooltip>
	</Marker>
);

// pass markerSelected to <SearchInput> component when marker's popup open
const handlePopupOpen = (cluster, dispatch) => {
	// setPointSelected(cluster);
	dispatch({
		type: Act.SET_POINT_SELECT,
		pointSelected: cluster,
	});
};

// generate point
const pointGenerator = ({
	clusterIndex,
	cluster,
	latitude,
	longitude,
	clusterPoints,
	dispatch,
	isLocNameDisplay,
	paletteStyle,
}) => {
	if (cluster && cluster.properties && cluster.properties.pubIds) {
		return (
			<Marker
				className="pointMarker"
				key={`cluster-${clusterIndex}-${cluster.properties.locId}`}
				position={[latitude, longitude]}
				icon={fetchPointIcon({
					count: cluster.properties.intensity,
					size: 10 + (cluster.properties.intensity / clusterPoints.length) * 130,
					locName: isLocNameDisplay ? cluster.properties.location : '',
					paletteStyle,
				})}
			>
				<Popup onOpen={() => handlePopupOpen(cluster, dispatch)}>
					{cluster.properties.location === '' || undefined ? 'Unknown' : cluster.properties.location}(
					{cluster.properties.intensity})
				</Popup>
				{/* <Tooltip className="mraker-tooltip"> */}
				{/*	<span style={{ fontWeight: 'bold' }}> */}
				{/*		{cluster.properties.location} ({cluster.properties.intensity}) */}
				{/*	</span> */}
				{/* </Tooltip> */}
			</Marker>
		);
	}
	return null;
};

const MapContainer = () => {
	const [state, dispatch] = useContext(StoreContext);
	const { map } = state;
	const { isLocNameDisplay, paletteStyle, publicationData, mapData, clusterPoints, showResultOnExport } = map;
	const [center, setCenter] = useState(mapDefault.center); // map center coordinate
	const [zoom, setZoom] = useState(window.screen.width > 767 ? 4 : 3); // zoom level
	const [bounds, setBounds] = useState(null); // map bounds
	const [heatmapRadius] = useState(heatmapDefault.heatmapRadius); // radius for heatmap
	const [heatmapBlur] = useState(heatmapDefault.heatmapBlur); // blur for heatmap
	const [max, setMax] = useState(heatmapDefault.max); // max for heatmap

	// set map element
	useEffect(() => {
		if (!_OpenStreetMap) return;
		dispatch({
			type: Act.SET_MAP_ELEMENT,
			mapElement: _OpenStreetMap,
		});
	}, [dispatch]);

	// set map center
	useEffect(() => {
		if (!(mapData && mapData.length > 0)) return;
		setCenter(getCenter(mapData));
		setMax(publicationData.length);
		setZoom(window.screen.width > 767 ? 4 : 3);
	}, [mapData, publicationData]);

	// get map bounds
	const updateMap = () => {
		if (!_OpenStreetMap || !_OpenStreetMap.leafletElement) {
			return;
		}

		const b = _OpenStreetMap.leafletElement.getBounds();
		setBounds([
			// eslint-disable-next-line no-underscore-dangle
			b._southWest.lng,
			// eslint-disable-next-line no-underscore-dangle
			b._southWest.lat,
			// eslint-disable-next-line no-underscore-dangle
			b._northEast.lng,
			// eslint-disable-next-line no-underscore-dangle
			b._northEast.lat,
		]);
		setZoom(_OpenStreetMap.leafletElement.getZoom());
	};

	// set clusters data for generating points and clusters
	const { clusters, supercluster } = useSupercluster({
		points: clusterPoints,
		bounds,
		zoom,
		options: {
			radius: 50,
			maxZoom: 8,
			minPoints: 3,
			map: (props) => ({
				// eslint-disable-next-line react/prop-types
				intensity: props.intensity, // sum up every location's intensity
				// eslint-disable-next-line react/prop-types
				location: props.location, // concat every location name
			}),
			reduce: (acc, props) => {
				// eslint-disable-next-line react/prop-types
				acc.intensity += props.intensity;
				// eslint-disable-next-line react/prop-types
				acc.location += `,${props.location}`;
				return acc;
			},
		},
	});

	return (
		<div className="maps">
			{/* SearchInput component */}
			{showResultOnExport && <SearchInput />}
			<Map
				/* eslint-disable-next-line no-return-assign */
				ref={(el) => (_OpenStreetMap = el)}
				center={center}
				zoom={zoom}
				className="map"
				doubleClickZoom
				dragging
				preferCanvas
				style={{ height: '100%' }}
				onMoveEnd={updateMap}
			>
				{/* zoom in & zoom out control */}
				<ZoomControl position="bottomright" />

				{/* base map layer */}
				<LayersControl position="bottomright">
					{/* leaflet providers ref: https://leaflet-extras.github.io/leaflet-providers/preview/ */}
					<LayersControl.BaseLayer name="OpenStreetMap" checked>
						<TileLayer
							attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
							url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
						/>
					</LayersControl.BaseLayer>
					<LayersControl.BaseLayer name="Stadia-smooth">
						<TileLayer
							attribution="Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap, iPC, USGS, FAO, NPS, NRCAN, GeoBase, Kadaster NL, Ordnance Survey, Esri Japan, METI, Esri China (Hong Kong), and the GIS User Community"
							url="https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png"
						/>
					</LayersControl.BaseLayer>
					<LayersControl.BaseLayer name="Stadia-bright">
						<TileLayer
							attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
							url="https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}{r}.png"
						/>
					</LayersControl.BaseLayer>
					{/* marker layer : always for user to switch displaying */}
					<LayersControl.Overlay name="Marker" checked>
						<FeatureGroup>
							{clusters.map((cluster, clusterIndex) => {
								// every cluster point has coordinates
								const [longitude, latitude] = cluster.geometry.coordinates;
								// the point may be either a cluster or a crime point
								const { cluster: isCluster, point_count: pointCount } = cluster.properties;
								// if isCluster is true, render cluster
								if (isCluster) {
									return clusterGenerator({
										clusterIndex,
										cluster,
										latitude,
										longitude,
										pointCount,
										clusterPoints,
										supercluster,
										paletteStyle,
									});
								}
								// if isCluster is false, render point
								return pointGenerator({
									cluster,
									latitude,
									longitude,
									clusterPoints,
									dispatch,
									isLocNameDisplay,
									paletteStyle,
								});
							})}
						</FeatureGroup>
					</LayersControl.Overlay>
					{/*  heatmap layer */}
					<LayersControl.Overlay name="Heatmap">
						<FeatureGroup color="purple">
							<HeatmapLayer
								fitBoundsOnLoad={false}
								fitBoundsOnUpdate={false}
								points={mapData}
								longitudeExtractor={(m) => m.position[1]}
								latitudeExtractor={(m) => m.position[0]}
								intensityExtractor={(m) => (m.intensity / max) * 10.0}
								gradient={{
									0.1: 'blue',
									0.5: 'green',
									0.8: 'yellow',
									1.0: 'red',
								}}
								radius={Number(heatmapRadius)}
								blur={Number(heatmapBlur)}
								max={0.015}
							/>
						</FeatureGroup>
					</LayersControl.Overlay>
				</LayersControl>
			</Map>
		</div>
	);
};

export default MapContainer;
